import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomerService } from '../../services/customerService';
import { ActivityService } from '../../services/activityService';
import { useAuth } from '../../contexts/AuthContext';
import { Customer } from '../../types/agent';
import {
  CustomerForm,
  CustomerFormData,
  emptyCustomerFormData,
  validateCustomerForm
} from '../../components/CustomerForm';
import './AddCustomerPage.css';

export const EditCustomerPage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { currentAgent } = useAuth();

  const [formData, setFormData] = useState<CustomerFormData>({ ...emptyCustomerFormData });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const loadCustomer = useCallback(async () => {
    if (!customerId) return;

    setInitialLoading(true);
    try {
      const customer = await CustomerService.getCustomer(customerId);
      if (!customer) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Customer not found',
          life: 3000
        });
        navigate('/agent/customers');
        return;
      }

      setFormData({
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phone: customer.phone || '',
        status: customer.status,
        source: customer.source || 'other',
        leadScore: customer.leadScore || 50,
        notes: customer.notes || '',
        tags: customer.tags || [],
        income: customer.profileData?.income || customer.income,
        creditScore: customer.profileData?.creditScore || customer.creditScore,
        employmentStatus: customer.profileData?.employmentStatus || '',
        homeOwnershipStatus: customer.profileData?.homeOwnershipStatus || '',
        location: customer.profileData?.location || '',
        mortgageBalance: customer.mortgageBalance,
        homeValue: customer.homeValue
      });
    } catch (error) {
      console.error('Error loading customer:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load customer details',
        life: 3000
      });
      navigate('/agent/customers');
    } finally {
      setInitialLoading(false);
    }
  }, [customerId, navigate, toast]);

  useEffect(() => {
    loadCustomer();
  }, [loadCustomer]);

  const validateForm = (): boolean => {
    const newErrors = validateCustomerForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fix the errors in the form',
        life: 3000
      });
      return;
    }

    setLoading(true);

    try {
      const customerData: Partial<Customer> = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        status: formData.status,
        source: formData.source,
        leadScore: formData.leadScore,
        notes: formData.notes.trim(),
        tags: formData.tags,
        profileData: {
          income: formData.income,
          creditScore: formData.creditScore,
          employmentStatus: formData.employmentStatus,
          homeOwnershipStatus: formData.homeOwnershipStatus,
          location: formData.location?.trim()
        },
        mortgageBalance: formData.mortgageBalance,
        homeValue: formData.homeValue
      };

      await CustomerService.updateCustomer(customerId!, customerData);

      // Log activity
      if (currentAgent?.id) {
        const customerName = `${formData.firstName} ${formData.lastName}`.trim();
        await ActivityService.logActivity(
          currentAgent.id,
          'customer_edited',
          'customer',
          customerId!,
          customerName,
          {
            customerEmail: formData.email
          }
        );
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Customer updated successfully',
        life: 3000
      });

      // Navigate to the customer details page after a short delay
      setTimeout(() => {
        navigate(`/agent/customers/${customerId}`);
      }, 1500);

    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update customer. Please try again.',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: keyof CustomerFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (initialLoading) {
    return (
      <div className="add-customer-page">
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <i className="pi pi-spinner pi-spin" style={{ fontSize: '2rem' }}></i>
          <p>Loading customer details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="add-customer-page">
      <Toast ref={toast} />

      <div className="page-header">
        <h1>Edit Customer</h1>
        <p>Update customer information</p>
      </div>

      <CustomerForm
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/agent/customers/${customerId}`)}
        loading={loading}
        submitLabel="Save Changes"
        submitIcon="pi pi-save"
      />
    </div>
  );
};

export default EditCustomerPage;
